import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_ORGANIZATION_PLOT_GROUP_COMPARISONS_PATH,
  GetOrganizationPlotGroupComparisonsErrorDto,
  GetOrganizationPlotGroupComparisonsSuccessDto,
} from '@/api/rest/resources/types/organization';
import { useWorkstreamId } from '@/pages/shared/home/hooks/useWorkstreamId';
import { assertIsDefined } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

type SuccessDto = GetOrganizationPlotGroupComparisonsSuccessDto;
type ErrorDto = GetOrganizationPlotGroupComparisonsErrorDto;

export const useOrganizationPlotGroupComparisons = (organizationIdArg: string) => {
  const id = organizationIdArg;
  const workstreamId = useWorkstreamId();

  assertIsDefined(id, 'organizationId');

  const queryParams = {
    ...(workstreamId ? { workstream_id: workstreamId } : {}),
  };

  const queryFn = () => {
    return getRequest<SuccessDto>(GET_ORGANIZATION_PLOT_GROUP_COMPARISONS_PATH, { organization_id: id }, queryParams);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['organization', id, 'plot-group-comparisons'],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useOrganizationPlotGroupComparison = (organizationId: string, plotGroupComparisonId?: string | null) => {
  const plotGroupComparisons = useOrganizationPlotGroupComparisons(organizationId).data;

  if (isNullish(plotGroupComparisonId)) {
    return null;
  }
  return plotGroupComparisons.find((group) => group.id === plotGroupComparisonId);
};
